/**
 * PlatformVariants
 * Logic for generating platform-specific image variants
 * 
 * This module handles the generation of optimized images for each social media platform.
 * Each platform has specific aspect ratio and dimension requirements.
 */

import { 
  PlatformId, 
  PlatformVariant, 
  CropArea, 
  MediaEditorVariants,
  AspectRatioOption,
  ImageCropPosition,
  ImageFitMode
} from './types';
import { 
  PLATFORM_CONFIGS, 
  FEED_CONFIG, 
  ALTERNATIVE_DIMENSIONS,
  getPlatformConfig,
  ERROR_MESSAGES
} from './constants';
import { 
  generateAutoCropVariant, 
  generateBlurContainVariant,
  generateCustomCropVariant,
  cleanupUrls 
} from './imageProcessor';

// ============================================================================
// FEED VARIANT
// ============================================================================

/**
 * Generate feed variant (canonical 4:5 format for SuperKonnected feed)
 * 
 * @param file - Original image file
 * @returns Promise resolving to PlatformVariant
 */
export async function generateFeedVariant(file: File): Promise<PlatformVariant> {
  const { aspectRatio, dimensions } = FEED_CONFIG;
  
  const blobUrl = await generateAutoCropVariant(
    file,
    aspectRatio,
    dimensions.width,
    dimensions.height
  );
  
  return {
    platform: 'feed',
    blobUrl,
    aspectRatio,
    dimensions
  };
}

function normalizeFitMode(fitMode?: ImageFitMode): ImageFitMode {
  return fitMode || 'crop';
}

// ============================================================================
// PLATFORM VARIANTS
// ============================================================================

/**
 * Generate auto-cropped variant for a specific platform
 * Uses center crop to automatically frame the image
 * 
 * @param file - Original image file
 * @param platformId - Platform identifier
 * @param customAspect - Optional custom aspect ratio for platforms with alternatives
 * @returns Promise resolving to PlatformVariant
 * @throws Error if platform is unknown
 */
export async function generatePlatformVariant(
  file: File,
  platformId: PlatformId,
  customAspect?: AspectRatioOption,
  fitMode?: ImageFitMode,
  cropPosition?: ImageCropPosition
): Promise<PlatformVariant> {
  const config = getPlatformConfig(platformId);
  
  if (!config) {
    throw new Error(`${ERROR_MESSAGES.UNKNOWN_PLATFORM}: ${platformId}`);
  }
  
  // Use custom aspect if provided and valid, otherwise use default
  let aspectRatio = config.aspectRatio;
  let dimensions = config.dimensions;
  
  if (customAspect && config.alternativeAspects?.includes(customAspect)) {
    aspectRatio = customAspect;
    dimensions = ALTERNATIVE_DIMENSIONS[customAspect];
  }
  
  const normalizedFitMode = normalizeFitMode(fitMode);
  const blobUrl = normalizedFitMode === 'blur'
    ? await generateBlurContainVariant(file, aspectRatio, dimensions.width, dimensions.height)
    : await generateAutoCropVariant(file, aspectRatio, dimensions.width, dimensions.height, cropPosition);
  
  return {
    platform: platformId,
    blobUrl,
    aspectRatio,
    dimensions
  };
}

/**
 * Generate custom-cropped variant for a specific platform
 * Uses user-defined crop area instead of auto center crop
 * 
 * @param file - Original image file
 * @param platformId - Platform identifier
 * @param croppedAreaPixels - Crop area from react-easy-crop
 * @param customAspect - Optional custom aspect ratio
 * @returns Promise resolving to PlatformVariant
 * @throws Error if platform is unknown
 */
export async function generateCustomPlatformVariant(
  file: File,
  platformId: PlatformId,
  croppedAreaPixels: CropArea,
  customAspect?: AspectRatioOption
): Promise<PlatformVariant> {
  const config = getPlatformConfig(platformId);
  
  if (!config) {
    throw new Error(`${ERROR_MESSAGES.UNKNOWN_PLATFORM}: ${platformId}`);
  }
  
  // Use custom aspect if provided, otherwise use default
  let aspectRatio = config.aspectRatio;
  let dimensions = config.dimensions;
  
  if (customAspect && config.alternativeAspects?.includes(customAspect)) {
    aspectRatio = customAspect;
    dimensions = ALTERNATIVE_DIMENSIONS[customAspect];
  }
  
  const blobUrl = await generateCustomCropVariant(
    file,
    croppedAreaPixels,
    dimensions.width,
    dimensions.height
  );
  
  return {
    platform: platformId,
    blobUrl,
    aspectRatio,
    dimensions
  };
}

// ============================================================================
// BATCH GENERATION
// ============================================================================

/**
 * Generate all variants for selected platforms
 * Always generates feed variant first, then platform-specific variants
 * 
 * @param file - Original image file
 * @param selectedPlatforms - Array of platform IDs to generate variants for
 * @param aspectOverrides - Optional aspect ratio overrides per platform
 * @param onProgress - Optional progress callback (platform, index, total)
 * @returns Promise resolving to MediaEditorVariants
 */
export async function generateAllVariants(
  file: File,
  selectedPlatforms: PlatformId[],
  aspectOverrides?: Record<PlatformId, AspectRatioOption>,
  onProgress?: (platform: string, index: number, total: number) => void,
  fitMode?: ImageFitMode | Partial<Record<PlatformId, ImageFitMode>>,
  cropPositions?: Partial<Record<PlatformId, ImageCropPosition>>
): Promise<MediaEditorVariants> {
  const variants: MediaEditorVariants = {
    feed: '',
    original: file
  };
  
  const totalSteps = selectedPlatforms.length + 1; // +1 for feed
  let currentStep = 0;
  
  try {
    // Generate feed variant first
    onProgress?.('feed', currentStep, totalSteps);
    const feedVariant = await generateFeedVariant(file);
    variants.feed = feedVariant.blobUrl;
    currentStep++;
    
    // Generate platform variants
    for (const platformId of selectedPlatforms) {
      onProgress?.(platformId, currentStep, totalSteps);
      
      const customAspect = aspectOverrides?.[platformId];
      const platformFitMode = typeof fitMode === 'string' ? fitMode : fitMode?.[platformId];
      const variant = await generatePlatformVariant(file, platformId, customAspect, platformFitMode, cropPositions?.[platformId]);
      
      variants[platformId] = variant.blobUrl;
      currentStep++;
    }
    
    return variants;
  } catch (error) {
    // Cleanup any generated URLs on error
    cleanupUrls([
      variants.feed,
      variants.linkedin,
      variants.x,
      variants.facebook,
      variants.instagram
    ]);
    throw error;
  }
}

/**
 * Update a single platform variant with custom crop
 * Cleans up the old URL before generating new one
 * 
 * @param currentVariants - Current variants object
 * @param file - Original image file
 * @param platformId - Platform to update
 * @param croppedAreaPixels - Crop area from react-easy-crop
 * @param customAspect - Optional custom aspect ratio
 * @returns Promise resolving to updated MediaEditorVariants
 */
export async function updatePlatformVariant(
  currentVariants: MediaEditorVariants,
  file: File,
  platformId: PlatformId,
  croppedAreaPixels: CropArea,
  customAspect?: AspectRatioOption
): Promise<MediaEditorVariants> {
  // Cleanup old URL for this platform
  const oldUrl = currentVariants[platformId];
  if (oldUrl) {
    cleanupUrls([oldUrl]);
  }
  
  // Generate new variant
  const variant = await generateCustomPlatformVariant(
    file,
    platformId,
    croppedAreaPixels,
    customAspect
  );
  
  return {
    ...currentVariants,
    [platformId]: variant.blobUrl
  };
}

// ============================================================================
// UI HELPERS
// ============================================================================

/**
 * Get platform display info for UI
 * 
 * @param platformId - Platform identifier
 * @returns Platform display info or undefined if platform not found
 */
export function getPlatformDisplayInfo(platformId: PlatformId): {
  name: string;
  icon: string;
  aspectRatio: string;
  hasAlternatives: boolean;
  alternatives?: AspectRatioOption[];
} | undefined {
  const config = getPlatformConfig(platformId);
  
  if (!config) return undefined;
  
  return {
    name: config.name,
    icon: config.icon,
    aspectRatio: config.aspectRatio,
    hasAlternatives: !!config.alternativeAspects?.length,
    alternatives: config.alternativeAspects
  };
}

/**
 * Get all platform configs for UI display
 * 
 * @returns Array of platform configs
 */
export function getAllPlatformConfigs() {
  return PLATFORM_CONFIGS;
}

/**
 * Get dimensions for a specific aspect ratio
 * 
 * @param aspectRatio - Aspect ratio option
 * @returns Dimensions object { width, height }
 */
export function getDimensionsForAspect(aspectRatio: AspectRatioOption): { width: number; height: number } {
  return ALTERNATIVE_DIMENSIONS[aspectRatio];
}


























